import { CustomerUpdateStatus, IconNames } from "@/types/v2";
import { ClassNameValue } from "tailwind-merge";

// Status badge styling consistent with table columns
export const statusClasses: Record<CustomerUpdateStatus, ClassNameValue> = {
  [CustomerUpdateStatus.CREATED]: "bg-blue-600 text-blue-100 hover:bg-blue-700",
  [CustomerUpdateStatus.SCHEDULED]:
    "bg-accent/80 text-accent-foreground hover:bg-accent/90",
  [CustomerUpdateStatus.PENDING]: "bg-warning text-white hover:bg-warning/70",
  [CustomerUpdateStatus.SNOOZED]:
    "bg-orange-600 text-orange-100 hover:bg-orange-700",
  [CustomerUpdateStatus.COMPLETED]: "bg-success/60 text-white hover:bg-success",
  [CustomerUpdateStatus.SKIPPED]: "bg-gray-600 text-gray-100 hover:bg-gray-700",
  [CustomerUpdateStatus.CANCELLED]: "bg-success/80 text-white hover:bg-success",
};

export const getStatusIcon = (status: CustomerUpdateStatus) => {
  switch (status) {
    case CustomerUpdateStatus.PENDING:
      return IconNames.clockWarn;
    case CustomerUpdateStatus.SNOOZED:
      return IconNames.clockFade;
    case CustomerUpdateStatus.SCHEDULED:
      return IconNames.clockUp;
    case CustomerUpdateStatus.CREATED:
      return IconNames.clockAdd;
    case CustomerUpdateStatus.COMPLETED:
      return IconNames.circleCheck;
    case CustomerUpdateStatus.SKIPPED:
      return IconNames.rotateCw;
    case CustomerUpdateStatus.CANCELLED:
      return IconNames.circleCheck;
    default:
      return IconNames.bell;
  }
};

export interface CustomerUpdatesFilters {
  userId?: string;
  status?: CustomerUpdateStatus | "all";
  cardNumberFilter?: string;
}

export interface CustomerUpdatesSorting {
  sortBy?: string | null;
  sortOrder?: "asc" | "desc";
}

/**
 * Builds search options array for Firestore queries based on filters
 */
export const buildCustomerUpdatesSearchOptions = (
  filters: CustomerUpdatesFilters
) => {
  const searchOptions = [];

  if (filters.userId) {
    searchOptions.push({
      searchParam: "userId",
      opsString: "==" as const,
      equality: filters.userId,
    });
  }

  if (filters.status && filters.status !== "all") {
    searchOptions.push({
      searchParam: "status",
      opsString: "==" as const,
      equality: filters.status,
    });
  }

  if (filters.cardNumberFilter && filters.cardNumberFilter.trim()) {
    const trimmedFilter = filters.cardNumberFilter.trim();
    searchOptions.push({
      searchParam: "cardNumber",
      opsString: ">=" as const,
      equality: trimmedFilter,
    });
    searchOptions.push({
      searchParam: "cardNumber",
      opsString: "<" as const,
      equality: trimmedFilter + "\uf8ff", // High Unicode character for range end
    });
  }

  return searchOptions;
};

/**
 * Gets the default sorting configuration
 */
export const getDefaultCustomerUpdatesSorting = (): CustomerUpdatesSorting => ({
  sortBy: "createdAt",
  sortOrder: "desc",
});

/**
 * Normalizes sorting parameters
 */
export const normalizeCustomerUpdatesSorting = (
  sorting: CustomerUpdatesSorting
): CustomerUpdatesSorting => ({
  sortBy: sorting.sortBy ?? "createdAt",
  sortOrder: sorting.sortOrder ?? "desc",
});

/**
 * Fetches all customer updates with the given filters and sorting
 * This is a direct function call, not a React Query hook
 */
export const fetchAllCustomerUpdates = async (
  shopId: string,
  filters: CustomerUpdatesFilters,
  sorting: CustomerUpdatesSorting
): Promise<import("@/types/v2").CustomerUpdate[]> => {
  const { firestore } = await import("@/services/firestore");

  // Build search options and normalize sorting
  const searchOptions = buildCustomerUpdatesSearchOptions(filters);
  const normalizedSorting = normalizeCustomerUpdatesSorting(sorting);

  let allDocs: import("@/types/v2").CustomerUpdate[] = [];
  let hasMore = true;
  let lastDoc = undefined;
  const pageSize = 100; // Fetch in batches of 100

  // Fetch all documents in batches
  while (hasMore) {
    let result;

    if (searchOptions.length > 0) {
      // For filtered queries
      result = await firestore.paginateDocumentsWithFilters<
        import("@/types/v2").CustomerUpdate
      >(
        `/shops/${shopId}/customerUpdates`,
        pageSize,
        lastDoc,
        undefined, // converter
        normalizedSorting.sortBy ?? undefined,
        normalizedSorting.sortOrder,
        searchOptions
      );
    } else {
      // No filters, use existing method
      result = await firestore.paginateDocuments<
        import("@/types/v2").CustomerUpdate
      >(
        `/shops/${shopId}/customerUpdates`,
        pageSize,
        lastDoc,
        undefined, // converter
        normalizedSorting.sortBy ?? undefined,
        normalizedSorting.sortOrder
      );
    }

    allDocs = [...allDocs, ...result.docs];
    hasMore = result.hasMore;
    lastDoc = result.lastVisible;
  }

  return allDocs;
};
